import type { DeckardIcon, DeckardIconProps } from '@assets/icons';
import { Tooltip, type TooltipProps } from '@components/tooltip';
import {
  IconButton as MuiIconButton,
  useTheme,
  type IconButtonProps as MuiIconButtonProps,
} from '@mui/material';
import { forwardRef, type Ref } from 'react';

export type IconButtonProps = Omit<MuiIconButtonProps, 'size' | 'color'> & {
  Icon: DeckardIcon;
  size?: number;
  tooltip?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'>;
  variant?: 'default' | 'error' | 'success';
  iconProps?: DeckardIconProps;
  color?: string;
};

export const IconButton = forwardRef(
  (
    {
      Icon,
      size = 18,
      tooltip,
      tooltipPlacement = 'top',
      tooltipProps,
      sx,
      variant = 'default',
      iconProps,
      color,
      ...props
    }: IconButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const { palette } = useTheme();
    const baseColor = color || palette.icon[variant];
    return tooltip ? (
      <Tooltip
        title={tooltip}
        placement={tooltipPlacement}
        enterDelay={400}
        enterNextDelay={400}
        {...tooltipProps}
      >
        <span style={{ display: 'inline-block' }}>
          <MuiIconButton
            ref={ref}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            sx={{
              padding: '5px',
              border: '1px solid transparent',
              borderRadius: '50%',
              '&:hover': {
                borderColor: baseColor,
              },
              '&:active': {
                '& .deckard-icon': {
                  fill: baseColor,
                },
              },
              '&:focus': {
                outline: `3px solid ${palette.button.outline}`,
                '&:not(:focus-visible)': {
                  outline: 'none',
                },
              },
              ...sx,
            }}
            size="small"
            {...props}
          >
            <Icon
              size={size}
              color={props.disabled ? palette.icon.disabled : baseColor}
              {...iconProps}
            />
          </MuiIconButton>
        </span>
      </Tooltip>
    ) : (
      <MuiIconButton
        ref={ref}
        disableFocusRipple
        disableRipple
        disableTouchRipple
        sx={{
          padding: '5px',
          border: '1px solid transparent',
          borderRadius: '50%',
          '&:hover': {
            borderColor: baseColor,
          },
          '&:active': {
            '& .deckard-icon': {
              fill: baseColor,
            },
          },
          '&:focus': {
            outline: `3px solid ${palette.button.outline}`,
            '&:not(:focus-visible)': {
              outline: 'none',
            },
          },
          ...sx,
        }}
        size="small"
        {...props}
      >
        <Icon
          size={size}
          color={props.disabled ? palette.icon.disabled : baseColor}
          {...iconProps}
        />
      </MuiIconButton>
    );
  },
);
